import React, { useEffect, useState } from 'react';
import {
  Dialog,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Box,
  Button,
  Typography
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

function EditDerivativesToken({ token, open, setOpen }) {
  const [form, setForm] = useState(token);
  const [status, setStatus] = useState(token?.status ?? false);
  const [logo, setLogo] = useState(token?.logo ?? '');

  useEffect(() => {
    setForm(token);
    setStatus(token?.status ?? false);
    setLogo(token?.logo ?? '');
  }, [token]);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleClose = () => setOpen(false);

  const saveToken = () => {
    axios
      .put(`${url}/crypto/updateToken`, {
        ...form,
        status
      })
      .then((res) => alert(JSON.stringify(res.data)))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('fileName', file.name);

    axios.post(`${url}/hootdex/token-logo-upload`, formData).then((res) => {
      if (res.data.status === 'ok') {
        const uploaded = `${url}/hootdex/images/${res.data.fileName}`;
        setLogo(uploaded);
        setForm({ ...form, logo: uploaded });
      }
    });
  };

  if (token?.type !== 'DERIVATIVES') return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 2002 }}
    >
      <Box p={5} bgcolor="whitesmoke">
        {/* Logo */}
        <Box position="relative" mb={3}>
          <img src={logoWrapper(logo, token)} width={140} alt={form?.name} />
          <input hidden id="logo-upload" type="file" onChange={uploadLogo} />
          <label htmlFor="logo-upload">
            <Edit
              sx={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                cursor: 'pointer'
              }}
            />
          </label>
        </Box>

        {/* Status */}
        <Typography mb={2}>
          Status: {status ? 'Active' : 'Inactive'}
          <Switch
            checked={status}
            onChange={(e) => setStatus(e.target.checked)}
          />
        </Typography>

        {/* Basic Identity */}
        <TextField
          fullWidth
          label="Token Name"
          margin="normal"
          value={form?.name ?? ''}
          onChange={handleChange('name')}
        />

        <TextField
          fullWidth
          label="Symbol"
          margin="normal"
          value={form?.symbol ?? ''}
          onChange={handleChange('symbol')}
        />

        <TextField
          fullWidth
          type="number"
          label="Total Supply"
          margin="normal"
          value={form?.amount ?? ''}
          onChange={handleChange('amount')}
        />

        <TreasuryLockStatusFields tokenType="DERIVATIVES" tokenId={form?.id} />

        {/* Issuer */}
        <TextField
          fullWidth
          label="Issuer Legal Name"
          margin="normal"
          value={form?.issuer_legal_name ?? ''}
          onChange={handleChange('issuer_legal_name')}
        />

        <TextField
          fullWidth
          label="Pair Identifier (A-B)"
          margin="normal"
          value={form?.pair_identifier ?? ''}
          onChange={handleChange('pair_identifier')}
        />

        {/* Pair Type */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="pair-type-label">Hybrid Type</InputLabel>
          <Select
            labelId="pair-type-label"
            label="Hybrid Type"
            value={form?.pair_type_code ?? ''}
            onChange={handleChange('pair_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="231101">L1-L1</MenuItem>
            <MenuItem value="231102">L1-FX</MenuItem>
            <MenuItem value="231103">L1-RWA</MenuItem>
            <MenuItem value="231104">FX-RWA</MenuItem>
            <MenuItem value="231105">FX-FX</MenuItem>
            <MenuItem value="231106">Cross-Asset Hybrid</MenuItem>
          </Select>
        </FormControl>

        {/* Pricing */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="pricing-label">Pricing Method</InputLabel>
          <Select
            labelId="pricing-label"
            label="Pricing Method"
            value={form?.pricing_type_code ?? ''}
            onChange={handleChange('pricing_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="232101">Direct Ratio (A/B)</MenuItem>
            <MenuItem value="232102">Inverse Ratio (B/A)</MenuItem>
            <MenuItem value="232103">Volatility Adjusted</MenuItem>
            <MenuItem value="232104">Liquidity Weighted</MenuItem>
            <MenuItem value="232105">Smart-Contract Oracle</MenuItem>
            <MenuItem value="232106">Multi-Oracle Median</MenuItem>
            <MenuItem value="232107">DAT Weighted</MenuItem>
          </Select>
        </FormControl>

        {/* Collateral */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="collateral-label">Treasury Collateral</InputLabel>
          <Select
            labelId="collateral-label"
            label="Treasury Collateral"
            value={form?.collateral_type_code ?? ''}
            onChange={handleChange('collateral_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="233101">Multi-Asset</MenuItem>
            <MenuItem value="233102">Digital-Only</MenuItem>
            <MenuItem value="233103">Stablecoin Heavy</MenuItem>
            <MenuItem value="233104">Volatility Hedged</MenuItem>
            <MenuItem value="233105">Yield Optimized</MenuItem>
            <MenuItem value="233106">Over-Collateralized</MenuItem>
          </Select>
        </FormControl>

        {/* Settlement */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="settlement-label">Settlement Type</InputLabel>
          <Select
            labelId="settlement-label"
            label="Settlement Type"
            value={form?.settlement_type_code ?? ''}
            onChange={handleChange('settlement_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="236101">DAT-Backed Redemption</MenuItem>
            <MenuItem value="236102">PECU Swap</MenuItem>
            <MenuItem value="236103">USXM Swap</MenuItem>
            <MenuItem value="236104">Smart-Contract Auto</MenuItem>
            <MenuItem value="236105">Composite Asset</MenuItem>
            <MenuItem value="236106">Single Asset</MenuItem>
          </Select>
        </FormControl>

        {/* Lifecycle */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="lifecycle-label">Lifecycle State</InputLabel>
          <Select
            labelId="lifecycle-label"
            label="Lifecycle State"
            value={form?.lifecycle_type_code ?? ''}
            onChange={handleChange('lifecycle_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="234101">Perpetual</MenuItem>
            <MenuItem value="234102">Active</MenuItem>
            <MenuItem value="234103">Paused</MenuItem>
            <MenuItem value="234104">Redeemable</MenuItem>
          </Select>
        </FormControl>

        {/* Description */}
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Description"
          margin="normal"
          value={form?.description ?? ''}
          onChange={handleChange('description')}
        />

        <Button
          fullWidth
          sx={{ mt: 4, bgcolor: 'darkgreen', color: 'white' }}
          onClick={saveToken}
        >
          Save Pairs
        </Button>
      </Box>
    </Dialog>
  );
}

export default EditDerivativesToken;
